import React, { useState, useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import TermsContent from '@/content/legal/TermsContent'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Mail,
  Phone,
  User,
  Lock,
  Globe,
  FileText,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  RefreshCw,
  Eye,
  EyeOff,
  X,
  ChevronDown,
  Building2
} from 'lucide-react'

const COUNTRY_CODES = [
  { code: '+52', country: 'México', flag: '🇲🇽', digits: 10, format: 'XX XXXX XXXX' },
  { code: '+1', country: 'USA/Canadá', flag: '🇺🇸', digits: 10, format: 'XXX XXX XXXX' },
  { code: '+34', country: 'España', flag: '🇪🇸', digits: 9, format: 'XXX XX XX XX' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷', digits: 10, format: 'XX XXXX XXXX' },
  { code: '+57', country: 'Colombia', flag: '🇨🇴', digits: 10, format: 'XXX XXX XXXX' },
  { code: '+56', country: 'Chile', flag: '🇨🇱', digits: 9, format: 'X XXXX XXXX' },
  { code: '+51', country: 'Perú', flag: '🇵🇪', digits: 9, format: 'XXX XXX XXX' },
  { code: '+507', country: 'Panamá', flag: '🇵🇦', digits: 8, format: 'XXXX XXXX' },
  { code: '+506', country: 'Costa Rica', flag: '🇨🇷', digits: 8, format: 'XXXX XXXX' },
  { code: '+595', country: 'Paraguay', flag: '🇵🇾', digits: 9, format: 'XXX XXX XXX' },
  { code: '+504', country: 'Honduras', flag: '🇭🇳', digits: 8, format: 'XXXX XXXX' },
  { code: '+502', country: 'Guatemala', flag: '🇬🇹', digits: 8, format: 'XXXX XXXX' },
  { code: '+503', country: 'El Salvador', flag: '🇸🇻', digits: 8, format: 'XXXX XXXX' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨', digits: 9, format: 'XXX XXX XXX' },
]

const Register: React.FC = () => {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  const selectedPlan = searchParams.get('plan') || 'free'

  // Detectar flujo OAuth
  const oauthProvider = searchParams.get('provider')
  const oauthProviderId = searchParams.get('provider_id')
  const oauthEmail = searchParams.get('email')
  const oauthName = searchParams.get('name')
  const oauthAvatar = searchParams.get('avatar')
  const isOAuthFlow = !!(oauthProvider && oauthProviderId && oauthEmail)

  // Estados del flujo
  const [currentStep, setCurrentStep] = useState(isOAuthFlow ? 3 : 1)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  // Paso 1: Datos iniciales
  const [initialData, setInitialData] = useState({
    name: oauthName || '',
    email: oauthEmail || '',
    phone: '',
    business_name: ''
  })
  const [countryCode, setCountryCode] = useState('+52')
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)
  const countryDropdownRef = useRef<HTMLDivElement>(null)

  // Paso 2: Verificación
  const [verificationCode, setVerificationCode] = useState('')
  const [resendCountdown, setResendCountdown] = useState(0)

  // Paso 3: Completar registro
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    subdomain: '',
    bio: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [subdomainStatus, setSubdomainStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable'>('idle')
  const [subdomainMessage, setSubdomainMessage] = useState('')

  // Paso 4: Términos
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [canAcceptTerms, setCanAcceptTerms] = useState(false)
  const termsRef = useRef<HTMLDivElement>(null)

  const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0]

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCountdown])

  // Sugerencia de subdominio: se pide al backend onBlur, NO en cada tecla
  const fetchSubdomainSuggestion = async () => {
    if (!initialData.name.trim() && !initialData.business_name.trim()) return
    try {
      const response = await fetch('/api/auth/suggest-subdomain', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: initialData.name, business_name: initialData.business_name })
      })
      const data = await response.json()
      if (data.success && data.subdomain) {
        setFormData(prev => ({ ...prev, subdomain: data.subdomain }))
      }
    } catch (err) {
      console.error('Error sugiriendo subdominio:', err)
    }
  }

  useEffect(() => {
    if (!formData.subdomain || formData.subdomain.length < 3) {
      setSubdomainStatus('idle')
      return
    }
    const timer = setTimeout(async () => {
      setSubdomainStatus('checking')
      try {
        const response = await fetch(`/api/auth/check-subdomain/${formData.subdomain}`)
        const data = await response.json()
        if (data.available) {
          setSubdomainStatus('available')
          setSubdomainMessage(t('available'))
        } else {
          setSubdomainStatus('unavailable')
          setSubdomainMessage(t('unavailable'))
        }
      } catch (err) {
        setSubdomainStatus('idle')
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [formData.subdomain, t])

  const formatPhone = (value: string, digits: number) => {
    const nums = value.replace(/\D/g, '').slice(0, digits)
    if (digits === 10) {
      if (nums.length <= 3) return nums
      if (nums.length <= 6) return `${nums.slice(0, 3)} ${nums.slice(3)}`
      return `${nums.slice(0, 3)} ${nums.slice(3, 6)} ${nums.slice(6)}`
    } else if (digits === 9) {
      if (nums.length <= 3) return nums
      if (nums.length <= 6) return `${nums.slice(0, 3)} ${nums.slice(3)}`
      return `${nums.slice(0, 3)} ${nums.slice(3, 6)} ${nums.slice(6)}`
    } else if (digits === 8) {
      if (nums.length <= 4) return nums
      return `${nums.slice(0, 4)} ${nums.slice(4)}`
    }
    return nums
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value, selectedCountry.digits)
    setInitialData(prev => ({ ...prev, phone: formatted }))
  }

  const handleCountrySelect = (code: string) => {
    setCountryCode(code)
    setShowCountryDropdown(false)
    setInitialData(prev => ({ ...prev, phone: '' }))
  }

  const getFullPhone = () => {
    const phoneDigits = initialData.phone.replace(/\D/g, '')
    const codeDigits = countryCode.replace('+', '')
    return codeDigits + phoneDigits
  }

  const handleSendVerification = async () => {
    setError('')
    if (!initialData.name.trim()) { setError(t('errNameRequired')); return }
    if (!initialData.email || !/\S+@\S+\.\S+/.test(initialData.email)) { setError(t('errEmailInvalid')); return }
    const phoneDigits = initialData.phone.replace(/\D/g, '')
    if (phoneDigits.length < selectedCountry.digits) {
      setError(t('errPhoneDigits', { digits: selectedCountry.digits, country: selectedCountry.country }))
      return
    }
    setIsLoading(true)
    try {
      const response = await fetch('/api/auth/send-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: initialData.name, email: initialData.email, phone: getFullPhone() })
      })
      const data = await response.json()
      if (data.success) { setCurrentStep(2); setResendCountdown(60) }
      else setError(data.message || t('errSendCode'))
    } catch (err) {
      setError(t('errConnection'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyCode = async () => {
    setError('')
    if (verificationCode.length !== 6) { setError(t('errCodeLength')); return }
    setIsLoading(true)
    try {
      const response = await fetch('/api/auth/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: initialData.email, code: verificationCode })
      })
      const data = await response.json()
      if (data.success && data.verified) setCurrentStep(3)
      else setError(data.message || t('errCodeWrong'))
    } catch (err) {
      setError(t('errConnection'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    if (resendCountdown > 0) return
    setIsLoading(true)
    try {
      const response = await fetch('/api/auth/send-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: initialData.name, email: initialData.email, phone: getFullPhone() })
      })
      const data = await response.json()
      if (data.success) { setResendCountdown(60); setError('') }
      else setError(data.message || t('errResendCode'))
    } catch (err) {
      setError(t('errConnection'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleShowTerms = () => {
    // Flujo OAuth: no requiere contraseña
    if (!isOAuthFlow) {
      if (!formData.password || formData.password.length < 6) { setError(t('errPasswordLength')); return }
      if (formData.password !== formData.confirmPassword) { setError(t('errPasswordMatch')); return }
    }
    if (!formData.subdomain || formData.subdomain.length < 3) { setError(t('errSubdomainLength')); return }
    if (subdomainStatus !== 'available') { setError(t('errSubdomainUnavailable')); return }
    setError('')
    setShowTermsModal(true)
    setCanAcceptTerms(false)
  }

  const handleTermsScroll = () => {
    if (termsRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = termsRef.current
      if (scrollTop + clientHeight >= scrollHeight - 50) setCanAcceptTerms(true)
    }
  }

  const handleCreateAccount = async (termsAccepted: boolean) => {
    setShowTermsModal(false)
    setIsLoading(true)
    setError('')
    try {
      const body: any = {
        name: initialData.name,
        email: initialData.email,
        phone: getFullPhone(),
        subdomain: formData.subdomain.toLowerCase(),
        bio: formData.bio,
        business_name: initialData.business_name || null,
        terms_accepted: termsAccepted,
        plan_preference: selectedPlan !== 'free' ? selectedPlan : null
      }
      if (isOAuthFlow) {
        body.oauth_provider = oauthProvider
        body.oauth_provider_id = oauthProviderId
        body.avatar_url = oauthAvatar || ''
        body.password = Math.random().toString(36) + Date.now().toString(36)
        body.email_verified = true
      } else {
        body.password = formData.password
      }

      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (data.success) {
        if (isOAuthFlow) {
          localStorage.removeItem('token')
          localStorage.removeItem('agent')
          window.location.href = '/login?oauth_registered=true'
          return
        }
        const loginResponse = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: initialData.email, password: formData.password })
        })
        const loginData = await loginResponse.json()
        if (loginData.success && loginData.token) {
          localStorage.removeItem('token')
          localStorage.removeItem('agent')
          localStorage.setItem('token', loginData.token)
          if (loginData.agent) localStorage.setItem('agent', JSON.stringify(loginData.agent))

          if (selectedPlan && selectedPlan !== 'free') {
            try {
              const checkoutRes = await fetch('/api/billing/create-checkout', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${loginData.token}`
                },
                body: JSON.stringify({ planId: `${selectedPlan}_mx` })
              })
              const checkoutData = await checkoutRes.json()
              if (checkoutData.checkoutUrl) {
                window.location.href = checkoutData.checkoutUrl
                return
              }
            } catch (checkoutErr) {
              console.error('Error al crear checkout:', checkoutErr)
            }
          }
          window.location.href = '/dashboard'
        } else if (loginData.requires_terms) {
          window.location.href = '/dashboard'
        } else {
          window.location.href = '/login'
        }
      } else {
        setError(data.message || t('errCreateAccount'))
      }
    } catch (err) {
      setError(t('errConnection'))
    } finally {
      setIsLoading(false)
    }
  }

  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[1, 2, 3].map((step) => (
        <React.Fragment key={step}>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all ${
            currentStep >= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
          }`}>
            {currentStep > step ? <CheckCircle className="w-5 h-5" /> : step}
          </div>
          {step < 3 && (
            <div className={`w-12 h-1 rounded ${currentStep > step ? 'bg-blue-600' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4 flex items-center justify-center">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src="https://raw.githubusercontent.com/ruxo-rod/logos/main/logo%20IA.jpg"
              alt="InnovaAgent"
              className="mx-auto h-16 w-16 rounded-xl shadow-lg mb-4"
            />
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">{t('createAccount')}</h1>
          <p className="text-slate-400">
            {currentStep === 1 && t('step1Subtitle')}
            {currentStep === 2 && t('step2Subtitle')}
            {currentStep === 3 && t('step3Subtitle')}
          </p>
          {selectedPlan !== 'free' && (
            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">
              {t('selectedPlan')} <span className="font-semibold capitalize">{selectedPlan}</span>
            </div>
          )}
        </div>

        <StepIndicator />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <AnimatePresence mode="wait">

            {/* PASO 1 */}
            {currentStep === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('fullName')}</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="text" value={initialData.name} onChange={(e) => setInitialData(prev => ({ ...prev, name: e.target.value }))} onBlur={fetchSubdomainSuggestion}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder={t('fullNamePlaceholder')} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('businessName')} <span className="text-gray-400 font-normal">{t('optional')}</span></label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="text" value={initialData.business_name} onChange={(e) => setInitialData(prev => ({ ...prev, business_name: e.target.value }))} onBlur={fetchSubdomainSuggestion}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder={t('businessNamePlaceholder')} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('email')}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="email" value={initialData.email} onChange={(e) => setInitialData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder={t('emailPlaceholder')} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('phone', { digits: selectedCountry.digits })}</label>
                  <div className="flex gap-2">
                    <div className="relative" ref={countryDropdownRef}>
                      <button type="button" onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                        className="flex items-center gap-2 px-3 py-3 border border-gray-300 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors min-w-[110px]">
                        <span className="text-xl">{selectedCountry.flag}</span>
                        <span className="text-sm font-medium text-gray-700">{selectedCountry.code}</span>
                        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {showCountryDropdown && (
                          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                            className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                            <div className="max-h-64 overflow-y-auto">
                              {COUNTRY_CODES.map((country) => (
                                <button key={country.code} type="button" onClick={() => handleCountrySelect(country.code)}
                                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left ${countryCode === country.code ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}>
                                  <span className="text-xl">{country.flag}</span>
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-800">{country.country}</span>
                                    <span className="text-xs text-gray-500 ml-2">{country.code}</span>
                                  </div>
                                  {countryCode === country.code && <CheckCircle className="w-4 h-4 text-blue-500" />}
                                </button>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input type="tel" value={initialData.phone} onChange={handlePhoneChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder={selectedCountry.format.replace(/X/g, '0')} maxLength={selectedCountry.digits + 3} />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t('phoneExample')} {selectedCountry.code} {selectedCountry.format.replace(/X/g, '0')}</p>
                </div>
                {error && <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-xl text-sm"><AlertCircle className="w-5 h-5 flex-shrink-0" />{error}</div>}
                <button onClick={handleSendVerification} disabled={isLoading}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><span>{t('validateData')}</span><ArrowRight className="w-5 h-5" /></>}
                </button>
                <p className="text-center text-sm text-gray-500">{t('haveAccount')}{' '}<Link to="/login" className="text-blue-600 hover:underline font-medium">{t('signIn')}</Link></p>
              </motion.div>
            )}

            {/* PASO 2 */}
            {currentStep === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-blue-600" />
                  </div>
                  <p className="text-gray-600">{t('codeSentTo')}</p>
                  <p className="font-semibold text-gray-900">{initialData.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-center">{t('verificationCode')}</label>
                  <input type="text" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full text-center text-3xl font-mono tracking-[0.5em] py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="000000" maxLength={6} />
                </div>
                {error && <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-xl text-sm"><AlertCircle className="w-5 h-5 flex-shrink-0" />{error}</div>}
                <button onClick={handleVerifyCode} disabled={isLoading || verificationCode.length !== 6}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><span>{t('verifyCode')}</span><ArrowRight className="w-5 h-5" /></>}
                </button>
                <div className="flex items-center justify-between">
                  <button onClick={() => setCurrentStep(1)} className="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm">
                    <ArrowLeft className="w-4 h-4" />{t('changeEmail')}
                  </button>
                  <button onClick={handleResendCode} disabled={resendCountdown > 0 || isLoading} className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm disabled:text-gray-400">
                    <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    {resendCountdown > 0 ? t('resendIn', { seconds: resendCountdown }) : t('resendCode')}
                  </button>
                </div>
              </motion.div>
            )}

            {/* PASO 3 */}
            {currentStep === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">

                {isOAuthFlow ? (
                  <div className="p-3 bg-blue-50 text-blue-700 rounded-xl text-sm flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    {t('googleVerified')} {initialData.email}
                  </div>
                ) : (
                  <div className="p-3 bg-green-50 text-green-700 rounded-xl text-sm flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    {t('emailVerified')} {initialData.email}
                  </div>
                )}

                {!isOAuthFlow && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('password')}</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input type={showPassword ? 'text' : 'password'} value={formData.password}
                          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                          className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          placeholder={t('passwordPlaceholder')} />
                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('confirmPassword')}</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input type={showPassword ? 'text' : 'password'} value={formData.confirmPassword}
                          onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          placeholder={t('confirmPasswordPlaceholder')} />
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('subdomain')}</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="text" value={formData.subdomain}
                      onChange={(e) => setFormData(prev => ({ ...prev, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40) }))}
                      className={`w-full pl-10 pr-10 py-3 border rounded-xl focus:ring-2 transition-all ${
                        subdomainStatus === 'available' ? 'border-green-500 focus:ring-green-500' :
                        subdomainStatus === 'unavailable' ? 'border-red-500 focus:ring-red-500' :
                        'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
                      placeholder={t('subdomainPlaceholder')} />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {subdomainStatus === 'checking' && <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />}
                      {subdomainStatus === 'available' && <CheckCircle className="w-5 h-5 text-green-500" />}
                      {subdomainStatus === 'unavailable' && <AlertCircle className="w-5 h-5 text-red-500" />}
                    </div>
                  </div>
                  <p className={`text-sm mt-1 ${subdomainStatus === 'available' ? 'text-green-600' : subdomainStatus === 'unavailable' ? 'text-red-600' : 'text-gray-500'}`}>
                    {subdomainStatus !== 'idle' && subdomainStatus !== 'checking' ? subdomainMessage : t('yourSite', { subdomain: formData.subdomain || t('subdomainPlaceholder') })}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('bio')}</label>
                  <textarea value={formData.bio} onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))} rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                    placeholder={t('bioPlaceholder')} />
                </div>

                {error && <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-xl text-sm"><AlertCircle className="w-5 h-5 flex-shrink-0" />{error}</div>}

                <button onClick={handleShowTerms} disabled={isLoading}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><span>{t('createAccount')}</span><ArrowRight className="w-5 h-5" /></>}
                </button>
              </motion.div>
            )}

          </AnimatePresence>
        </motion.div>

        <p className="text-center mt-6">
          <Link to="/" className="text-slate-400 hover:text-white transition-colors">{t('backHome')}</Link>
        </p>
      </div>

      {/* MODAL TÉRMINOS */}
      <AnimatePresence>
        {showTermsModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
            onClick={() => setShowTermsModal(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="p-6 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">{t('termsTitle')}</h2>
                </div>
                <button onClick={() => setShowTermsModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div ref={termsRef} onScroll={handleTermsScroll}
                className="flex-1 overflow-y-auto p-6 text-gray-700 text-sm leading-relaxed">
                <TermsContent />
              </div>
              <div className="p-6 border-t bg-gray-50 rounded-b-2xl flex gap-4">
                <button onClick={() => setShowTermsModal(false)}
                  className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-xl transition-all">
                  {t('cancel')}
                </button>
                <button onClick={() => handleCreateAccount(true)} disabled={!canAcceptTerms || isLoading}
                  className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle className="w-5 h-5" /><span>{t('acceptAndCreate')}</span></>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Register
